import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { ProductSearchApiError } from "../api/client";
import type { WishlistCooldown, WishlistMutation } from "./contracts";
import { wishlistApi } from "./wishlistApi";

type WishlistState = {
  total: number;
  listRevision: number;
  cooldown: WishlistCooldown;
  authenticated: boolean | null;
  refresh: () => Promise<void>;
  applyMutation: (value: WishlistMutation) => void;
};

const emptyCooldown = { active: false, until: null, remainingMs: 0 };
const Context = createContext<WishlistState | null>(null);

export function WishlistProvider({ children }: { children: ReactNode }) {
  const [total, setTotal] = useState(0);
  const [listRevision, setRevision] = useState(0);
  const [cooldown, setCooldown] = useState<WishlistCooldown>(emptyCooldown);
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const refresh = useCallback(async () => {
    try {
      const value = await wishlistApi.summary();
      setTotal(value.total); setRevision(value.listRevision); setCooldown(value.singleDeleteCooldown); setAuthenticated(true);
    } catch (error) {
      if (error instanceof ProductSearchApiError && error.status === 401) setAuthenticated(false);
      else throw error;
    }
  }, []);
  useEffect(() => {
    void refresh().catch(() => undefined);
    const onVisible = () => { if (document.visibilityState === "visible") void refresh().catch(() => undefined); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refresh]);
  const applyMutation = useCallback((value: WishlistMutation) => {
    setTotal(value.total); setRevision(value.listRevision);
    if (value.singleDeleteCooldown) setCooldown(value.singleDeleteCooldown);
  }, []);
  const value = useMemo(() => ({ total, listRevision, cooldown, authenticated, refresh, applyMutation }), [total, listRevision, cooldown, authenticated, refresh, applyMutation]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useWishlist() {
  const value = useContext(Context);
  if (!value) throw new Error("WishlistProvider is missing");
  return value;
}
